import React, { useState, useEffect } from "react";
import { Tabs, Button, Table, Space, message, Switch, Modal, Form, Input, InputNumber, Select, Tooltip, Typography } from "antd";
import { EditOutlined, DeleteOutlined, PlusOutlined, MinusCircleOutlined } from "@ant-design/icons";
import { LineConfigAPI, TelegramConfigAPI, ConfigFilterOptAPI, ConfigWarningOptAPI } from "../constants/GenericAPI";
import { tooltip } from "leaflet";

const CommunicationSettings = ({ isActive }) => {
  const [lineSettings, setLineSettings] = useState([]);
  const [tgSettings, setTGSettings] = useState([]);
  const [filterSettings, setFilterSettings] = useState([]);
  const [warningSettings, setWarningSettings] = useState([]);
  const [filteredLineSettings, setFilteredLineSettings] = useState([]);
  const [filteredTGSettings, setFilteredTGSettings] = useState([]);
  const [filteredFilterSettings, setFilteredFilterSettings] = useState([]);
  const [filteredWarningSettings, setFilteredWarningSettings] = useState([]);
  const [lineSearchText, setLineSearchText] = useState("");
  const [tgSearchText, setTGSearchText] = useState("");
  const [filterSearchText, setFilterSearchText] = useState("");
  const [warningSearchText, setWarningSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [lineModalVisible, setLineModalVisible] = useState(false);
  const [tgModalVisible, setTgModalVisible] = useState(false);
  const [filterModalVisible, setFilterModalVisible] = useState(false);
  const [warningModalVisible, setWarningModalVisible] = useState(false);
  const [selectedLineSetting, setSelectedLineSetting] = useState(null);
  const [selectedTGSetting, setSelectedTGSetting] = useState(null);
  const [selectedFilterSetting, setSelectedFilterSetting] = useState(null);
  const [selectedWarningSetting, setSelectedWarningSetting] = useState(null);
  const [lineForm] = Form.useForm();
  const [tgForm] = Form.useForm();
  const [filterForm] = Form.useForm();
  const [warningForm] = Form.useForm();

  // 辨識物件預設選項（label 中文顯示、value 英文給 YOLO）
  const DETECT_CLASS_OPTIONS = [
    { label: "吊車", value: "crane" },
    { label: "吊臂", value: "boom" },
    { label: "卡車", value: "truck" },
    { label: "挖土機", value: "excavator" },
    { label: "車子", value: "car" },
    { label: "猴子", value: "monkey" },
    { label: "狗", value: "dog" },
    { label: "貓", value: "cat" },
    { label: "鳥", value: "bird" },
    { label: "動物", value: "animal" },
    { label: "昆蟲", value: "insect" },
    { label: "人", value: "person" },
  ];

  // 獲取所有設定
  const fetchSettings = async () => {
    setLoading(true);
    try {
      const [lineRes, tgRes, filterRes, warningRes] = await Promise.all([LineConfigAPI.getAll(), TelegramConfigAPI.getAll(), ConfigFilterOptAPI.getAll(), ConfigWarningOptAPI.getAll()]);
      
      // 確保返回的數據是有效的
      if (lineRes?.data?.data) {
        const lineData = lineRes.data.data;
        setLineSettings(lineData);
        setFilteredLineSettings(lineData);
      } else {
        console.warn("獲取LINE設定時返回的數據格式不符預期", lineRes);
        setLineSettings([]);
        setFilteredLineSettings([]);
      }
      
      if (tgRes?.data?.data) {
        const tgData = tgRes.data.data;
        setTGSettings(tgData);
        setFilteredTGSettings(tgData);
      } else {
        console.warn("獲取Telegram設定時返回的數據格式不符預期", tgRes);
        setTGSettings([]);
        setFilteredTGSettings([]);
      }

      if (filterRes?.data?.data) {
        const filterData = filterRes.data.data;
        setFilterSettings(filterData);
        setFilteredFilterSettings(filterData);
      } else {
        console.warn("獲取Filter設定時返回的數據格式不符預期", filterRes);
        setFilterSettings([]);
        setFilteredFilterSettings([]);
      }

      if (warningRes?.data?.data) {
        const warningData = warningRes.data.data;
        setWarningSettings(warningData);
        setFilteredWarningSettings(warningData);
      } else {
        console.warn("獲取警示語設定時返回的數據格式不符預期", warningRes);
        setWarningSettings([]);
        setFilteredWarningSettings([]);
      }
    } catch (error) {
      console.error("獲取設定失敗:", error);
      message.error("獲取設定失敗，請檢查網路連接");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  // 新增：當 isActive 變為 true 時重新獲取數據
  useEffect(() => {
    if (isActive) {
      fetchSettings();
    }
  }, [isActive]);

  // 處理 LINE 搜尋
  useEffect(() => {
    if (lineSearchText === "") {
      setFilteredLineSettings(lineSettings);
      return;
    }
    const filtered = lineSettings.filter(item => 
      item.setting_name?.toLowerCase().includes(lineSearchText.toLowerCase()) ||
      item.chat_id?.toLowerCase().includes(lineSearchText.toLowerCase())
    );
    setFilteredLineSettings(filtered);
  }, [lineSearchText, lineSettings]);

  // 處理 Telegram 搜尋
  useEffect(() => {
    if (tgSearchText === "") {
      setFilteredTGSettings(tgSettings);
      return;
    }
    const filtered = tgSettings.filter(item => 
      item.setting_name?.toLowerCase().includes(tgSearchText.toLowerCase()) ||
      item.chat_id?.toLowerCase().includes(tgSearchText.toLowerCase())
    );
    setFilteredTGSettings(filtered);
  }, [tgSearchText, tgSettings]);

  // 處理 Filter 搜尋
  useEffect(() => {
    if (filterSearchText === "") {
      setFilteredFilterSettings(filterSettings);
      return;
    }
    const filtered = filterSettings.filter(item => 
      item.name?.toLowerCase().includes(filterSearchText.toLowerCase()) ||
      item.chat_id?.toLowerCase().includes(filterSearchText.toLowerCase())
    );
    setFilteredFilterSettings(filtered);
  }, [filterSearchText, filterSettings]);

  // 處理警示語搜尋
  useEffect(() => {
    if (warningSearchText === "") {
      setFilteredWarningSettings(warningSettings);
      return;
    }
    const filtered = warningSettings.filter(item =>
      item.name?.toLowerCase().includes(warningSearchText.toLowerCase()) ||
      item.content?.toLowerCase().includes(warningSearchText.toLowerCase())
    );
    setFilteredWarningSettings(filtered);
  }, [warningSearchText, warningSettings]);

  // LINE 設定相關處理函數
  const handleLineEdit = (record) => {
    setSelectedLineSetting(record);
    lineForm.setFieldsValue({
      ...record,
      is_active: record.is_active === 1,
      warnings: parseWarnings(record.warnings),
    });
    setLineModalVisible(true);
  };

  const parseWarnings = (warningsStr) => {
    if (typeof warningsStr !== "string") {
      return warningsStr;
    }

    try {
      // 移除所有引號包裹
      let cleaned = warningsStr.replace(/^["']+|["']+$/g, "");
      // 移除多餘的跳脫字元
      cleaned = cleaned.replace(/\\/g, "");
      // 再次移除可能剩餘的引號包裹
      cleaned = cleaned.replace(/^["']+|["']+$/g, "");

      return JSON.parse(cleaned);
    } catch (e) {
      console.error("Warning parsing error:", e);
      // 如果解析失敗，返回預設值
      return [
        {
          threshold: 50,
          message: "訊息量已達 50%，欲購買流量請洽本公司02-7751-5063。",
        },
        {
          threshold: 80,
          message: "訊息量已達 80%，欲購買流量請洽本公司02-7751-5063。",
        },
      ];
    }
  };

  const handleLineDelete = (record) => {
    Modal.confirm({
      title: "確認刪除",
      content: "確定要刪除此 LINE 設定嗎？此操作無法復原。",
      okText: "確認",
      cancelText: "取消",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await LineConfigAPI.delete(record.id);
          setLineSettings((prev) => prev.filter((item) => item.id !== record.id));
          message.success("LINE 設定已刪除");
        } catch (error) {
          console.error("刪除 LINE 設定失敗:", error);
          message.error("刪除失敗");
        }
      },
    });
  };

  const handleLineStatusChange = async (checked, record) => {
    try {
      await LineConfigAPI.updateStatus(record.id, checked ? 1 : 0);
      setLineSettings((prev) => prev.map((item) => (item.id === record.id ? { ...item, is_active: checked ? 1 : 0 } : item)));
      message.success(`已${checked ? "啟用" : "停用"}該 LINE 設定`);
    } catch (error) {
      console.error("更新狀態失敗:", error);
      message.error("更新狀態失敗");
    }
  };

  const handleLineSubmit = async () => {
    try {
      const values = await lineForm.validateFields();
      const formData = {
        ...values,
        is_active: values.is_active ? 1 : 0,
      };

      if (selectedLineSetting) {
        // 修改
        await LineConfigAPI.update(selectedLineSetting.id, formData);
        setLineSettings((prev) => prev.map((item) => (item.id === selectedLineSetting.id ? { ...item, ...formData } : item)));
        message.success("LINE 設定修改成功");
      } else {
        // 新增
        const response = await LineConfigAPI.create(formData);
        const newId = response.data.data.id;
        formData.id = newId; // 將新 ID 加入到 formData 中
        setLineSettings((prev) => [formData, ...prev]);
        message.success("LINE 設定新增成功");
      }
      setLineModalVisible(false);
      setSelectedLineSetting(null);
    } catch (error) {
      console.error("操作失敗:", error);
      message.error("操作失敗，請稍後再試");
    }
  };

  // Telegram 設定相關處理函數
  const handleTGEdit = (record) => {
    setSelectedTGSetting(record);
    tgForm.setFieldsValue({
      ...record,
      is_active: record.is_active === 1,
    });
    setTgModalVisible(true);
  };

  const handleTGDelete = (record) => {
    Modal.confirm({
      title: "確認刪除",
      content: "確定要刪除此 Telegram 設定嗎？此操作無法復原。",
      okText: "確認",
      cancelText: "取消",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await TelegramConfigAPI.delete(record.id);
          setTGSettings((prev) => prev.filter((item) => item.id !== record.id));
          message.success("Telegram 設定已刪除");
        } catch (error) {
          console.error("刪除 Telegram 設定失敗:", error);
          message.error("刪除失敗");
        }
      },
    });
  };

  const handleTGStatusChange = async (checked, record) => {
    try {
      await TelegramConfigAPI.updateStatus(record.id, checked ? 1 : 0);
      setTGSettings((prev) => prev.map((item) => (item.id === record.id ? { ...item, is_active: checked ? 1 : 0 } : item)));
      message.success(`已${checked ? "啟用" : "停用"}該 Telegram 設定`);
    } catch (error) {
      console.error("更新狀態失敗:", error);
      message.error("更新狀態失敗");
    }
  };

  const handleTGSubmit = async () => {
    try {
      const values = await tgForm.validateFields();
      const formData = {
        ...values,
        is_active: values.is_active ? 1 : 0,
      };

      if (selectedTGSetting) {
        // 修改
        await TelegramConfigAPI.update(selectedTGSetting.id, formData);
        setTGSettings((prev) => prev.map((item) => (item.id === selectedTGSetting.id ? { ...item, ...formData } : item)));
        message.success("Telegram 設定修改成功");
      } else {
        // 新增
        const response = await TelegramConfigAPI.create(formData);
        const newId = response.data.data.id;
        formData.id = newId;
        setTGSettings((prev) => [formData, ...prev]);
        message.success("Telegram 設定新增成功");
      }
      setTgModalVisible(false);
      setSelectedTGSetting(null);
    } catch (error) {
      console.error("操作失敗:", error);
      message.error("操作失敗，請稍後再試");
    }
  };

  // Filter 設定相關處理函數
  const handleFilterEdit = (record) => {
    if (record.id === 0) {
      return; // ID 為 0 的記錄不可編輯
    }
    setSelectedFilterSetting(record);
    filterForm.setFieldsValue({
      ...record,
      is_active: record.is_active === 1,
      is_positive_flag: record.is_positive_flag === 1,
      detect_backend: record.detect_backend ?? 0,
      detect_classes: record.detect_classes
        ? String(record.detect_classes).split(",").filter(Boolean)
        : [],
      require_crossing: record.require_crossing ?? 0,
      conf_threshold: record.conf_threshold ?? 0.30,
    });
    setFilterModalVisible(true);
  };

  const handleFilterDelete = (record) => {
    if (record.id === 0) {
      return; // ID 為 0 的記錄不可刪除
    }
    Modal.confirm({
      title: "確認刪除",
      content: "確定要刪除此篩選設定嗎？此操作無法復原。",
      okText: "確認",
      cancelText: "取消",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await ConfigFilterOptAPI.delete(record.id);
          setFilterSettings((prev) => prev.filter((item) => item.id !== record.id));
          message.success("篩選設定已刪除");
        } catch (error) {
          console.error("刪除篩選設定失敗:", error);
          message.error("刪除失敗");
        }
      },
    });
  };

  const handleFilterStatusChange = async (checked, record) => {
    if (record.id === 0) {
      return; // ID 為 0 的記錄不可編輯
    }
    try {
      await ConfigFilterOptAPI.updateStatus(record.id, checked ? 1 : 0);
      setFilterSettings((prev) => prev.map((item) => (item.id === record.id ? { ...item, is_active: checked ? 1 : 0 } : item)));
      message.success(`已${checked ? "啟用" : "停用"}該篩選設定`);
    } catch (error) {
      console.error("更新狀態失敗:", error);
      message.error("更新狀態失敗");
    }
  };

  const handleFilterSubmit = async () => {
    try {
      const values = await filterForm.validateFields();
      const backend = values.detect_backend ?? 0;
      const formData = {
        ...values,
        is_active: values.is_active ? 1 : 0,
        // 只有 AI API 模式 is_ai_api=1；YOLO/VLM 皆為 0（後端靠 detect_backend 分流）
        is_ai_api: backend === 0 ? 1 : 0,
        // 本地模型固定正向；AI API 沿用開關
        is_positive_flag: backend === 1 ? 1 : (values.is_positive_flag ? 1 : 0),
        detect_backend: backend,
        detect_classes: Array.isArray(values.detect_classes)
          ? values.detect_classes.join(",")
          : (values.detect_classes || ""),
        require_crossing: backend === 1 ? (values.require_crossing ?? 0) : 0,
        conf_threshold: values.conf_threshold ?? 0.30,
      };

      if (selectedFilterSetting) {
        // 修改
        console.log("Updating with data:", formData);
        await ConfigFilterOptAPI.update(selectedFilterSetting.id, formData);
        setFilterSettings((prev) => prev.map((item) => (item.id === selectedFilterSetting.id ? { ...item, ...formData } : item)));
        message.success("篩選設定修改成功");
      } else {
        // 新增
        console.log("Create with data:", formData);
        const response = await ConfigFilterOptAPI.create(formData);
        const newId = response.data.data.id;
        formData.id = newId;
        setFilterSettings((prev) => [formData, ...prev]);
        message.success("篩選設定新增成功");
      }
      setFilterModalVisible(false);
      setSelectedFilterSetting(null);
    } catch (error) {
      console.error("操作失敗:", error);
      message.error("操作失敗，請稍後再試");
    }
  };

  // ── 警示語範本（通報範本：警示文字 + 是否帶主旨）相關處理函數 ──────────────
  const handleWarningEdit = (record) => {
    setSelectedWarningSetting(record);
    warningForm.setFieldsValue({
      ...record,
      include_subject: record.include_subject === 1,
      is_active: record.is_active === 1,
    });
    setWarningModalVisible(true);
  };

  const handleWarningDelete = (record) => {
    Modal.confirm({
      title: "確認刪除",
      content: "確定要刪除此警示語範本嗎？已套用此範本的群組會自動改為不顯示警示語。建議改用「停用」。",
      okText: "確認",
      cancelText: "取消",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await ConfigWarningOptAPI.delete(record.id);
          setWarningSettings((prev) => prev.filter((item) => item.id !== record.id));
          message.success("警示語範本已刪除");
        } catch (error) {
          console.error("刪除警示語範本失敗:", error);
          message.error("刪除失敗");
        }
      },
    });
  };

  const handleWarningStatusChange = async (checked, record) => {
    try {
      await ConfigWarningOptAPI.updateStatus(record.id, checked ? 1 : 0);
      setWarningSettings((prev) => prev.map((item) => (item.id === record.id ? { ...item, is_active: checked ? 1 : 0 } : item)));
      message.success(`已${checked ? "啟用" : "停用"}該警示語範本`);
    } catch (error) {
      console.error("更新狀態失敗:", error);
      message.error("更新狀態失敗");
    }
  };

  const handleWarningSubmit = async () => {
    try {
      const values = await warningForm.validateFields();
      const formData = {
        ...values,
        include_subject: values.include_subject ? 1 : 0,
        is_active: values.is_active ? 1 : 0,
      };

      if (selectedWarningSetting) {
        await ConfigWarningOptAPI.update(selectedWarningSetting.id, formData);
        setWarningSettings((prev) => prev.map((item) => (item.id === selectedWarningSetting.id ? { ...item, ...formData } : item)));
        message.success("警示語範本修改成功");
      } else {
        const response = await ConfigWarningOptAPI.create(formData);
        const newId = response.data.data.id;
        formData.id = newId;
        setWarningSettings((prev) => [formData, ...prev]);
        message.success("警示語範本新增成功");
      }
      setWarningModalVisible(false);
      setSelectedWarningSetting(null);
    } catch (error) {
      console.error("操作失敗:", error);
      message.error("操作失敗，請稍後再試");
    }
  };

  // 更新告警訊息
  const updateWarningMessages = (planMessages) => {
    const messageCount = planMessages === 0 ? 200 : planMessages === 1 ? 400 : 600;

    const warnings = lineForm.getFieldValue("warnings") || [];
    const updatedWarnings = warnings.map((warning) => {
      if (warning && typeof warning.threshold === "number") {
        const count = Math.floor((messageCount * warning.threshold) / 100);
        return {
          ...warning,
          message: `訊息量已達 ${warning.threshold}% (${count}/${messageCount}則)，欲購買流量請洽本公司02-7751-5063。`,
        };
      }
      return warning;
    });

    lineForm.setFieldsValue({ warnings: updatedWarnings });
  };

  // LINE 設定表格列定義
  // chat_id 欄渲染：有值→顯示 ID；空值→顯示 webhook 觸發指令（灰字，反白複製貼到群組可自動帶入）
  const renderChatIdCell = (record, triggerPrefix) => {
    if (record.chat_id) {
      return record.chat_id;
    }
    const trigger = `${triggerPrefix}${record.id}`;
    return (
      <Tooltip title="尚未綁定群組。複製此指令貼到目標群組訊息，機器人會自動帶入 Chat ID">
        <Typography.Text type="secondary" italic>
          {trigger}
        </Typography.Text>
      </Tooltip>
    );
  };

  const lineColumns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 60,
      align: "center",
    },
    {
      title: "群組名稱",
      dataIndex: "setting_name",
      key: "setting_name",
    },
    {
      title: "Group Chat ID",
      dataIndex: "chat_id",
      key: "chat_id",
      ellipsis: true,
      render: (_, record) => renderChatIdCell(record, "linesetting#"),
    },
    {
      title: "狀態",
      dataIndex: "is_active",
      key: "is_active",
      width: 100,
      align: "center",
      render: (is_active, record) => <Switch checked={is_active === 1} onChange={(checked) => handleLineStatusChange(checked, record)} size="small" />,
    },
    {
      title: "操作",
      key: "action",
      width: 140,
      align: "center",
      render: (_, record) => (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <Space size="small">
            <Tooltip title="編輯">
              <Button type="text" icon={<EditOutlined />} onClick={() => handleLineEdit(record)} size="small" />
            </Tooltip>
            <Tooltip title="刪除">
              <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleLineDelete(record)} size="small" />
            </Tooltip>
          </Space>
        </div>
      ),
    },
  ];

  // Telegram 設定表格列定義
  const tgColumns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 60,
      align: "center",
    },
    {
      title: "群組名稱",
      dataIndex: "setting_name",
      key: "setting_name",
    },
    {
      title: "Group Chat ID",
      dataIndex: "chat_id",
      key: "chat_id",
      ellipsis: true,
      render: (_, record) => renderChatIdCell(record, "/set tgsetting#"),
    },
    {
      title: "狀態",
      dataIndex: "is_active",
      key: "is_active",
      width: 100,
      align: "center",
      render: (is_active, record) => <Switch checked={is_active === 1} onChange={(checked) => handleTGStatusChange(checked, record)} size="small" />,
    },
    {
      title: "操作",
      key: "action",
      width: 140,
      align: "center",
      render: (_, record) => (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <Space size="small">
            <Tooltip title="編輯">
              <Button type="text" icon={<EditOutlined />} onClick={() => handleTGEdit(record)} size="small" />
            </Tooltip>
            <Tooltip title="刪除">
              <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleTGDelete(record)} size="small" />
            </Tooltip>
          </Space>
        </div>
      ),
    },
  ];

  // Filter 設定表格列定義
  const filterColumns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 60,
      align: "center",
    },
    {
      title: "名稱",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "AI Prompt",
      dataIndex: "ai_prompt",
      key: "ai_prompt",
      ellipsis: true,
    },
    {
      title: "Chat ID",
      dataIndex: "chat_id",
      key: "chat_id",
      ellipsis: true,
    },
    {
      title: "偵測方式",
      dataIndex: "detect_backend",
      key: "detect_backend",
      render: (v) => (v === 1 ? "本地模型(YOLO)" : v === 2 ? "本地模型(VLM)" : "AI API"),
    },
    // {
    //   title: <Tooltip title="是否啟用 OpenAI 來判斷">	AI API</Tooltip>,
    //   dataIndex: "is_ai_api",
    //   key: "is_ai_api",
    //   width: 100,
    //   align: "center",
    //   render: (is_ai_api) => is_ai_api === 1 ? "是" : "否",
    // },
    {
      title: <Tooltip title="只有在啟用 OpenAI 時有作用">正向判斷</Tooltip>,
      dataIndex: "is_positive_flag",
      key: "is_positive_flag",
      width: 100,
      align: "center",
      render: (is_positive_flag) => is_positive_flag === 1 ? "是" : "否",
    },
    {
      title: "狀態",
      dataIndex: "is_active",
      key: "is_active",
      width: 100,
      align: "center",
      render: (is_active, record) => (
        <Switch 
          checked={is_active === 1} 
          onChange={(checked) => handleFilterStatusChange(checked, record)} 
          size="small" 
          disabled={record.id === 0}
        />
      ),
    },
    {
      title: "操作",
      key: "action",
      width: 140,
      align: "center",
      render: (_, record) => (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <Space size="small">
            {record.id !== 0 && (
              <>
                <Tooltip title="編輯">
                  <Button type="text" icon={<EditOutlined />} onClick={() => handleFilterEdit(record)} size="small" />
                </Tooltip>
                <Tooltip title="刪除">
                  <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleFilterDelete(record)} size="small" />
                </Tooltip>
              </>
            )}
          </Space>
        </div>
      ),
    },
  ];

  // 警示語範本表格列定義
  const warningColumns = [
    {
      title: "名稱",
      dataIndex: "name",
      key: "name",
      width: 180,
    },
    {
      title: "警示語內容",
      dataIndex: "content",
      key: "content",
      ellipsis: true,
    },
    {
      title: <Tooltip title="套用此範本時，payload 是否帶上 mail 主旨">含主旨</Tooltip>,
      dataIndex: "include_subject",
      key: "include_subject",
      width: 100,
      align: "center",
      render: (include_subject) => (include_subject === 1 ? "是" : "否"),
    },
    {
      title: "狀態",
      dataIndex: "is_active",
      key: "is_active",
      width: 100,
      align: "center",
      render: (is_active, record) => <Switch checked={is_active === 1} onChange={(checked) => handleWarningStatusChange(checked, record)} size="small" />,
    },
    {
      title: "操作",
      key: "action",
      width: 140,
      align: "center",
      render: (_, record) => (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <Space size="small">
            <Tooltip title="編輯">
              <Button type="text" icon={<EditOutlined />} onClick={() => handleWarningEdit(record)} size="small" />
            </Tooltip>
            <Tooltip title="刪除">
              <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleWarningDelete(record)} size="small" />
            </Tooltip>
          </Space>
        </div>
      ),
    },
  ];

  // 定義方案選項
  const messageOptions = [
    { value: 0, label: "常規用量0-200則" },
    { value: 1, label: "增額用量201-400則" },
    { value: 2, label: "高額用量401-600則" },
  ];

  const memberOptions = [
    { value: 0, label: "5-10人(中小型群組)" },
    { value: 1, label: "11-50人(中型群組)" },
    { value: 2, label: "51-100人(大型群組)" },
  ];

  // 定義標籤頁項目
  const tabItems = [
    {
      key: "line",
      label: "LINE 設定",
      children: (
        <>
          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button
              onClick={() => {
                setSelectedLineSetting(null);
                lineForm.resetFields();
                lineForm.setFieldsValue({
                  is_active: true,
                  plan_users: 0,
                  plan_messages: 0,
                  current_users: 0,
                  monthly_messages: 0,
                  warnings: [
                    { threshold: 50, message: `訊息量已達 50%，欲購買流量請洽本公司02-7751-5063。` },
                    { threshold: 80, message: `訊息量已達 80%，欲購買流量請洽本公司02-7751-5063。` },
                  ],
                });
                setLineModalVisible(true);
              }}
            >
              新增 LINE 設定
            </Button>
            <Input
              className="search-input"
              placeholder="搜尋群組名稱或 Chat ID"
              onChange={e => setLineSearchText(e.target.value)}
              style={{ width: 250 }}
            />
          </div>
          <Table
            columns={lineColumns}
            dataSource={filteredLineSettings}
            bordered
            rowKey="id"
            loading={loading}
            pagination={{
              defaultPageSize: 10,
              showTotal: (total) => `共 ${total} 筆資料`,
            }}
          />
        </>
      ),
    },
    {
      key: "telegram",
      label: "Telegram 設定",
      children: (
        <>
          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button
              onClick={() => {
                setSelectedTGSetting(null);
                tgForm.resetFields();
                tgForm.setFieldsValue({
                  is_active: true,
                });
                setTgModalVisible(true);
              }}
            >
              新增 Telegram 設定
            </Button>
            <Input
              className="search-input"
              placeholder="搜尋群組名稱或 Chat ID"
              onChange={e => setTGSearchText(e.target.value)}
              style={{ width: 250 }}
            />
          </div>
          <Table
            columns={tgColumns}
            dataSource={filteredTGSettings}
            rowKey="id"
            loading={loading}
            pagination={{
              defaultPageSize: 10,
              showTotal: (total) => `共 ${total} 筆資料`,
            }}
            bordered
          />
        </>
      ),
    },
    {
      key: "filter",
      label: "篩選設定",
      children: (
        <>
          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button
              onClick={() => {
                setSelectedFilterSetting(null);
                filterForm.resetFields();
                filterForm.setFieldsValue({
                  is_active: true,
                  is_ai_api: false,
                  is_positive_flag: false,
                });
                setFilterModalVisible(true);
              }}
            >
              新增篩選設定
            </Button>
            <Input
              placeholder="搜尋名稱 或 Chat ID"
              onChange={e => setFilterSearchText(e.target.value)}
              style={{ width: 250 }}
            />
          </div>
          <Table
            columns={filterColumns}
            dataSource={filteredFilterSettings}
            rowKey="id"
            loading={loading}
            pagination={{
              defaultPageSize: 10,
              showTotal: (total) => `共 ${total} 筆資料`,
            }}
            bordered
          />
        </>
      ),
    },
    {
      key: "warning",
      label: "警示語設定",
      children: (
        <>
          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button
              onClick={() => {
                setSelectedWarningSetting(null);
                warningForm.resetFields();
                warningForm.setFieldsValue({
                  include_subject: true,
                  is_active: true,
                });
                setWarningModalVisible(true);
              }}
            >
              新增警示語範本
            </Button>
            <Input
              placeholder="搜尋名稱 或 內容"
              onChange={e => setWarningSearchText(e.target.value)}
              style={{ width: 250 }}
            />
          </div>
          <Table
            columns={warningColumns}
            dataSource={filteredWarningSettings}
            rowKey="id"
            loading={loading}
            pagination={{
              defaultPageSize: 10,
              showTotal: (total) => `共 ${total} 筆資料`,
            }}
            bordered
          />
        </>
      ),
    },
  ];

  return (
    <div className="communication-settings">
      <Tabs items={tabItems} />

      {/* LINE 設定 Modal */}
      <Modal
        title={selectedLineSetting ? "編輯 LINE 設定" : "新增 LINE 設定"}
        open={lineModalVisible}
        onCancel={() => {
          setLineModalVisible(false);
          setSelectedLineSetting(null);
        }}
        onOk={handleLineSubmit}
        width={800}
      >
        <Form
          form={lineForm}
          layout="vertical"
          initialValues={{
            is_active: true,
            plan_users: 0,
            plan_messages: 0,
            current_users: 0,
            monthly_messages: 0,
          }}
        >
          <Form.Item name="setting_name" label="群組名稱" rules={[{ required: true, message: "請輸入群組名稱" }]}>
            <Input placeholder="請輸入群組名稱" />
          </Form.Item>

          <Form.Item name="channel_id" label="Channel ID" rules={[{ required: true, message: "請輸入 Channel ID" }]}>
            <Input placeholder="請輸入 Channel ID" />
          </Form.Item>

          <Form.Item name="channel_secret" label="Channel Secret" rules={[{ required: true, message: "請輸入 Channel Secret" }]}>
            <Input placeholder="請輸入 Channel Secret" />
          </Form.Item>

          <Form.Item name="access_token" label="Access Token" rules={[{ required: true, message: "請輸入 Access Token" }]}>
            <Input placeholder="請輸入 Access Token" />
          </Form.Item>

          <Form.Item name="chat_id" label="Group Chat ID" rules={[{ required: false, message: "請輸入客戶群組的id" }]}>
            <Input placeholder="請輸入 Group Chat ID" />
          </Form.Item>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
            <Form.Item name="plan_users" label="人數方案" rules={[{ required: true, message: "請選擇人數方案" }]}>
              <Select options={memberOptions} placeholder="請選擇人數方案" />
            </Form.Item>

            <Form.Item name="plan_messages" label="訊息量方案" rules={[{ required: true, message: "請選擇訊息量方案" }]}>
              <Select
                options={messageOptions}
                placeholder="請選擇訊息量方案"
                onChange={(value) => {
                  updateWarningMessages(value);
                }}
              />
            </Form.Item>

            <Form.Item name="current_users" label="目前用戶數" rules={[{ required: true, message: "請輸入目前用戶數" }]}>
              <InputNumber style={{ width: "100%" }} min={0} />
            </Form.Item>

            <Form.Item name="monthly_messages" label="已發送訊息數" rules={[{ required: true, message: "請輸入每月訊息數" }]}>
              <InputNumber style={{ width: "100%" }} min={0} />
            </Form.Item>
          </div>

          <Form.Item label="告警設置" required tooltip="設置不同百分比的告警閾值及對應訊息">
            <Form.List
              name="warnings"
              initialValue={[
                { threshold: 50, message: `訊息量已達 50%，欲購買流量請洽本公司02-7751-5063。` },
                { threshold: 80, message: `訊息量已達 80%，欲購買流量請洽本公司02-7751-5063。` },
              ]}
            >
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }, index) => (
                    <Space
                      key={key}
                      style={{
                        display: "flex",
                        marginBottom: 8,
                        alignItems: "flex-start", // 改為頂部對齊
                      }}
                    >
                      <Form.Item
                        {...restField}
                        name={[name, "threshold"]}
                        rules={[
                          { required: true, message: "請輸入百分比" },
                          ({ getFieldValue }) => ({
                            validator(_, value) {
                              const listValues = getFieldValue("warnings");
                              if (index === 0 || value > listValues[index - 1].threshold) {
                                return Promise.resolve();
                              }
                              return Promise.reject(new Error("新加入的百分比門檻需比前一列還高"));
                            },
                          }),
                        ]}
                        style={{ marginBottom: 0 }} // 移除底部邊距
                      >
                        <InputNumber
                          min={1}
                          max={100}
                          addonAfter="%"
                          style={{ width: 100 }}
                          onChange={(value) => {
                            const messageCount =
                              lineForm.getFieldValue("plan_messages") === 0 ? 200 : lineForm.getFieldValue("plan_messages") === 1 ? 400 : 600;
                            const count = Math.floor((messageCount * value) / 100);
                            const newMessage = `訊息量已達 ${value}% (${count}/${messageCount}則)，欲購買流量請洽本公司02-7751-5063。`;

                            const warnings = lineForm.getFieldValue("warnings");
                            warnings[name].message = newMessage;
                            lineForm.setFieldsValue({ warnings });
                          }}
                        />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, "message"]}
                        rules={[{ required: true, message: "請輸入告警訊息" }]}
                        style={{
                          width: 500,
                          marginBottom: 0, // 移除底部邊距
                        }}
                      >
                        <Input placeholder="告警訊息" />
                      </Form.Item>
                      {fields.length > 1 && (
                        <Button
                          onClick={() => remove(name)}
                          type="text"
                          danger
                          icon={<MinusCircleOutlined />}
                          style={{ marginTop: 4 }} // 微調按鈕位置
                        />
                      )}
                    </Space>
                  ))}
                  <Form.Item>
                    <Button type="dashed" onClick={() => add({ threshold: "", message: "" })} block icon={<PlusOutlined />}>
                      添加告警設置
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </Form.Item>

          <Form.Item name="is_active" valuePropName="checked" tooltip="設定是否啟用此 LINE 通知">
            <Switch checkedChildren="啟用" unCheckedChildren="停用" />
          </Form.Item>

          {selectedLineSetting && (
            <Form.Item name="id" hidden>
              <Input />
            </Form.Item>
          )}
        </Form>
      </Modal>

      {/* Telegram 設定 Modal */}
      <Modal
        title={selectedTGSetting ? "編輯 Telegram 設定" : "新增 Telegram 設定"}
        open={tgModalVisible}
        onCancel={() => {
          setTgModalVisible(false);
          setSelectedTGSetting(null);
        }}
        onOk={handleTGSubmit}
        width={600}
      >
        <Form
          form={tgForm}
          layout="vertical"
          initialValues={{
            is_active: true,
          }}
        >
          <Form.Item name="setting_name" label="群組名稱" rules={[{ required: true, message: "請輸入群組名稱" }]}>
            <Input placeholder="請輸入群組名稱" />
          </Form.Item>

          <Form.Item name="chat_id" label="Group Chat ID" rules={[{ required: false, message: "請輸入客戶群組的id" }]}>
            <Input placeholder="請輸入 Group Chat ID" />
          </Form.Item>

          <Form.Item name="is_active" valuePropName="checked" tooltip="設定是否啟用此 Telegram 通知">
            <Switch checkedChildren="啟用" unCheckedChildren="停用" />
          </Form.Item>

          {selectedTGSetting && (
            <Form.Item name="id" hidden>
              <Input />
            </Form.Item>
          )}
        </Form>
      </Modal>

      {/* Filter 設定 Modal */}
      <Modal
        title={selectedFilterSetting ? "編輯篩選設定" : "新增篩選設定"}
        open={filterModalVisible}
        onCancel={() => {
          setFilterModalVisible(false);
          setSelectedFilterSetting(null);
        }}
        onOk={handleFilterSubmit}
        width={600}
      >
        <Form
          form={filterForm}
          layout="vertical"
          initialValues={{
            is_active: true,
            is_positive_flag: false,
            detect_backend: 0,
            detect_classes: [],
            require_crossing: 0,
            conf_threshold: 0.30,
          }}
        >
          <Form.Item name="name" label="名稱" rules={[{ required: true, message: "請輸入名稱" }]}>
            <Input placeholder="請輸入名稱" />
          </Form.Item>

          <Form.Item name="description" label="描述" rules={[{ required: true, message: "請輸入描述" }]}>
            <Input placeholder="請輸入描述" />
          </Form.Item>

          <Form.Item name="chat_id" label="篩選群 Chat ID" tooltip="篩選未命中時轉發的群組" rules={[{ required: false }]}>
            <Input placeholder="請輸入 Chat ID" />
          </Form.Item>

          <Form.Item name="detect_backend" label="偵測方式" rules={[{ required: true }]}>
            <Select
              options={[
                { label: "AI API（OpenAI 判斷有／無）", value: 0 },
                { label: "本地模型（YOLO 偵測目標框）", value: 1 },
                { label: "本地模型（VLM 是非判斷）", value: 2 },
              ]}
            />
          </Form.Item>

          {/* 依偵測方式切換欄位 */}
          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.detect_backend !== cur.detect_backend}>
            {({ getFieldValue }) =>
              getFieldValue("detect_backend") === 1 ? (
                <>
                  <Form.Item
                    name="detect_classes"
                    label="辨識物件"
                    tooltip="可多選預設項目；自訂新增請輸入英文詞（YOLO 類別）"
                    rules={[{ required: true, message: "請至少選一個辨識物件" }]}
                  >
                    <Select
                      mode="tags"
                      placeholder="選擇或輸入要辨識的物件"
                      options={DETECT_CLASS_OPTIONS}
                      tokenSeparators={[","]}
                    />
                  </Form.Item>

                  <Form.Item name="require_crossing" label="觸發方式" rules={[{ required: true }]}>
                    <Select
                      options={[
                        { label: "圖中有就發", value: 0 },
                        { label: "越線才發", value: 1 },
                      ]}
                    />
                  </Form.Item>

                  <Form.Item noStyle shouldUpdate={(p, c) => p.require_crossing !== c.require_crossing}>
                    {({ getFieldValue: g }) =>
                      g("require_crossing") === 1 ? (
                        <Typography.Text
                          type="secondary"
                          style={{ display: "block", marginTop: -12, marginBottom: 12 }}
                        >
                          ※ 若套用此篩選的攝影機未劃設警戒線，將自動退回「圖中有就發」
                        </Typography.Text>
                      ) : null
                    }
                  </Form.Item>

                  <Form.Item name="conf_threshold" label="信心門檻（0~1）" tooltip="越高越嚴格、雜訊越少；預設 0.3">
                    <InputNumber min={0} max={1} step={0.05} style={{ width: "100%" }} />
                  </Form.Item>
                </>
              ) : (
                <>
                  <Form.Item
                    name="ai_prompt"
                    label="AI Prompt"
                    tooltip="判斷用的 prompt,回答應為 1/0;本地 VLM 與 AI API 皆適用"
                    rules={[{ required: false }]}
                  >
                    <Input.TextArea placeholder={`請輸入 AI Prompt\n回答應為 "是/否"、"1/0"`} rows={3} />
                  </Form.Item>

                  <Form.Item name="is_positive_flag" label="設定是否為正向標記" valuePropName="checked">
                    <Switch checkedChildren="是" unCheckedChildren="否" />
                  </Form.Item>
                </>
              )
            }
          </Form.Item>

          <Form.Item name="is_active" label="設定是否啟用此篩選" valuePropName="checked">
            <Switch checkedChildren="啟用" unCheckedChildren="停用" />
          </Form.Item>

          {selectedFilterSetting && (
            <Form.Item name="id" hidden>
              <Input />
            </Form.Item>
          )}
        </Form>
      </Modal>

      {/* 警示語範本 Modal */}
      <Modal
        title={selectedWarningSetting ? "編輯警示語範本" : "新增警示語範本"}
        open={warningModalVisible}
        onCancel={() => {
          setWarningModalVisible(false);
          setSelectedWarningSetting(null);
        }}
        onOk={handleWarningSubmit}
        width={600}
      >
        <Form
          form={warningForm}
          layout="vertical"
          initialValues={{
            include_subject: true,
            is_active: true,
          }}
        >
          <Form.Item name="name" label="名稱" rules={[{ required: true, message: "請輸入名稱" }]} tooltip="群組下拉選單顯示的名稱">
            <Input placeholder="例如：鷹架區域警示" />
          </Form.Item>

          <Form.Item name="content" label="警示語內容" rules={[{ required: true, message: "請輸入警示語內容" }]} tooltip="實際顯示在通報第二行（⚠️ 開頭）的文字">
            <Input.TextArea placeholder="例如：鷹架區域請注意人員安全" rows={3} />
          </Form.Item>

          <Form.Item name="include_subject" label="是否帶上 mail 主旨" valuePropName="checked" tooltip="開啟：通報會多一行顯示原始郵件主旨；關閉：只顯示表頭與警示語">
            <Switch checkedChildren="含主旨" unCheckedChildren="不含主旨" />
          </Form.Item>

          <Form.Item name="is_active" label="設定是否啟用此範本" valuePropName="checked">
            <Switch checkedChildren="啟用" unCheckedChildren="停用" />
          </Form.Item>

          {selectedWarningSetting && (
            <Form.Item name="id" hidden>
              <Input />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default CommunicationSettings;